import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  FormFields,
  FormFieldsGeneratorService
} from '../../../shared/services/form-fields-generator.service';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA,
  MatLegacyDialogRef
} from '@angular/material/legacy-dialog';
import { ConfigurationSettingsContextService } from '../../../shared/services/configuration-settings-context.service';
import { PrivateDocumentsListService } from './../../../personal-archive/services/private-documents-list.service';

@Component({
  selector: 'ab-upload-document',
  templateUrl: './upload-document.component.html',
  styleUrls: ['./upload-document.component.scss']
})
export class UploadDocumentComponent implements OnInit {
  @ViewChild('file') file: ElementRef;

  formFields: FormFields;

  public allowedIdCardCharactersRegex: RegExp = undefined;
  public maxIdLenght: number = undefined;
  public uploadForm = new UntypedFormGroup({
    documentType: new UntypedFormControl('', Validators.required),
    files: new UntypedFormControl(),
    documentSubType: new UntypedFormControl('', Validators.required),
    documentNumber: new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(this.maxIdLenght),
      Validators.pattern(this.allowedIdCardCharactersRegex)
    ]),
    expirationDate: new UntypedFormControl('', Validators.required)
  });

  public allowedExtensions: Array<string> = [];
  public previewableExtenstions: Array<string> = ['jpg', 'jpeg', 'png'];
  public maxFileCount: number;
  public maxFileSize: number;
  public maxFileNameSize: number;
  public expirationDateMin: Date;
  public isMaxFileCountReached: boolean;
  public isMaxFileNameCountReached: boolean;
  public isFilesExtensionsCorrect: boolean;
  public isFilesSizesCorrect: boolean;
  public initialState: boolean;
  public isInTheZone: boolean;

  public fileList: Array<File> = [];
  public filesPreviews: Array<any> = [];
  public showDocumentSubType: boolean;

  get documentType() {
    return this.uploadForm.get('documentType');
  }

  get documentSubType() {
    return this.uploadForm.get('documentSubType');
  }

  get documentNumber() {
    return this.uploadForm.get('documentNumber');
  }

  get expirationDate() {
    return this.uploadForm.get('expirationDate');
  }

  constructor(
    @Inject(MAT_LEGACY_DIALOG_DATA) public data: any,
    public dialogRef: MatLegacyDialogRef<UploadDocumentComponent>,
    private privateDocumentsListService: PrivateDocumentsListService,
    private configurationSettingsContextService: ConfigurationSettingsContextService,
    private formFieldsGeneratorService: FormFieldsGeneratorService
  ) {
    this.documentType.setValue(
      this.data.fields.DocumentType[0].fields.Value.value
    );
  }

  ngOnInit() {
    this.setFields(this.data.fields.DocumentType[0].fields.Value.value);
    this.formFields = this.formFieldsGeneratorService.generateFormFields(
      this.data
    ) as FormFields;

    this.setAllowedExtensions();
    this.setMaxFileCount();
    this.setMaxFileSize();
    this.setMaxFileNameSize();
    this.setExpirationDateMin();
    this.allowedIdCardCharactersRegex = new RegExp(
      this.validationDNumberAlllowedCharacters
    );
    this.maxIdLenght = this.validationIDNumberLenght;
    this.isMaxFileCountReached = false;
    this.isMaxFileNameCountReached = false;
    this.isFilesExtensionsCorrect = true;
    this.isFilesSizesCorrect = true;
    this.initialState = true;

    this.documentType.valueChanges.subscribe((value) => this.setFields(value));
  }

  setAllowedExtensions() {
    this.allowedExtensions =
      this.data.fields.allowedFileExtensions.value.split(',');
  }

  setMaxFileCount() {
    this.maxFileCount = 5;
  }

  setMaxFileSize() {
    this.maxFileSize = this.data.fields.maxFileSize.value;
  }

  setMaxFileNameSize() {
    this.maxFileNameSize = parseInt(
      this.data.fields.maxFileNameAndExtensionSize.value,
      0
    );
  }

  setExpirationDateMin() {
    this.expirationDateMin = new Date(
      new Date().setMonth(
        new Date().getMonth() +
          parseInt(this.data.fields.minIdValidityDuration.value)
      )
    );
  }

  setFields(value: any) {
    if (value === this.data.fields.DocumentType[0].fields.Value.value) {
      this.showDocumentSubType = true;
      this.documentSubType.enable();
      this.documentNumber.enable();
      this.expirationDate.enable();
    } else {
      this.showDocumentSubType = false;
      this.documentSubType.disable();
      this.documentNumber.disable();
      this.expirationDate.disable();
    }
  }

  openUploadWindow($event: any) {
    $event.preventDefault();
    this.file.nativeElement.click();
  }

  removeFile(index: number) {
    this.fileList.splice(index, 1);
    this.filesPreviews.splice(index, 1);
    this.isMaxFileCountReached = false;
    this.isMaxFileNameCountReached = false;
    this.isFilesExtensionsCorrect = true;
    this.isFilesSizesCorrect = true;
    for (let i = 0; i <= this.fileList.length - 1; i++) {
      const file = this.fileList[i];
      if (!this.validateExtension(file)) {
        this.isFilesExtensionsCorrect = false;
      }
      if (!this.validateSize(file)) {
        this.isFilesSizesCorrect = false;
      }
    }
    if (this.fileList.length == 0) {
      this.initialState = true;
    }
  }

  removeFiles() {
    this.fileList = [];
    this.filesPreviews = [];
    this.isMaxFileCountReached = false;
    this.isMaxFileNameCountReached = false;
    this.isFilesExtensionsCorrect = true;
    this.isFilesSizesCorrect = true;
    this.initialState = true;
  }

  onFilesSelect($event: any) {
    this.createFilesArray($event.target.files);
    this.generateFilePreviews();
    this.file.nativeElement.value = '';
  }

  onFilesDrop($event: any) {
    this.createFilesArray($event.dataTransfer.files);
    this.generateFilePreviews();
  }

  createFilesArray(files: Array<File>) {
    let filesLenght = this.fileList.length + files.length;
    if (filesLenght > this.maxFileCount) {
      this.isMaxFileCountReached = true;
      this.initialState = false;
      return false;
    }
    this.initialState = false;
    this.isMaxFileCountReached = false;

    let filesArray: Array<File> = [];

    for (let i = 0; i <= files.length - 1; i++) {
      const file = files[i];
      if (!this.validateExtension(file)) {
        this.isFilesExtensionsCorrect = false;
      }
      if (!this.validateSize(file)) {
        this.isFilesSizesCorrect = false;
      }
      filesArray.push(file);
    }

    this.fileList = this.fileList.concat(filesArray);
  }

  generateFilePreviews() {
    this.fileList.forEach((file, index) => {
      if (this.isPreviewable(file)) {
        const reader = new FileReader();

        reader.onload = (event: any) => {
          this.filesPreviews[index] = event.target.result;
        };

        reader.readAsDataURL(file);
      } else {
        this.filesPreviews[index] = false;
      }
    });
  }

  validateFile(file: File): boolean {
    return (
      this.validateExtension(file) &&
      this.validateSize(file) &&
      this.validateFilenameSize(file)
    );
  }

  validateExtension(file: File): boolean {
    let ext = file.name.split('.')[file.name.split('.').length - 1];
    return this.allowedExtensions.lastIndexOf(ext.toLowerCase()) != -1;
  }

  validateSize(file: File): boolean {
    return file.size / 1024 <= 1024 * this.maxFileSize;
  }

  validateFilenameSize(file: File): boolean {
    if (!this.isMaxFileNameCountReached) {
      this.isMaxFileNameCountReached = file.name.length > this.maxFileNameSize;
    }

    return file.name.length <= this.maxFileNameSize;
  }

  isPreviewable(file: File): boolean {
    let ext = file.name.split('.')[file.name.split('.').length - 1];
    return this.previewableExtenstions.lastIndexOf(ext.toLowerCase()) != -1;
  }

  get validationIDNumberLenght() {
    return parseFloat(
      this.configurationSettingsContextService?.getConfigurationSettingsValidationAttributeValue(
        'IDNumberLenght'
      )
    );
  }

  get validationDNumberAlllowedCharacters() {
    return this.configurationSettingsContextService?.getConfigurationSettingsValidationAttributeValue(
      'IDNumberAllowedCharacters'
    );
  }

  submit() {
    const formData: FormData = new FormData();

    this.fileList.forEach((file) => {
      formData.append('document', file, file.name);
    });

    formData.append('documentType', this.documentType.value);

    if (this.showDocumentSubType) {
      formData.append('idDocumentType', this.documentSubType.value);
      formData.append('documentNumber', this.documentNumber.value);
      formData.append(
        'expirationDate',
        this.expirationDate.value.toISOString()
      );
    }

    this.privateDocumentsListService.uploadDocuments(formData).subscribe(() => {
      this.dialogRef.close(true);
    });
  }

  onDragOver(event) {
    this.isInTheZone = true;
    event.preventDefault();
    event.stopPropagation();
  }
  onDragLeave(event) {
    this.isInTheZone = false;
    event.preventDefault();
    event.stopPropagation();
  }
  onDrop(event) {
    this.isInTheZone = false;
    event.preventDefault();
    event.stopPropagation();
  }
}
